import { useEffect, useState } from "react";
import { Calendar, DataCard, Button, CalendarEntry, CalendarTimeType, CalendarViewType } from "mueller-controls";
import "./index.scss";
import { DateRange, Phone, Info, PhoneCallback } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { getAppointmentInformation, postAppointmentSelection, AutomaticAppointmentDto, AutomaticAppointmentStatus } from "requests/automaticAppointment";
import moment, { Moment } from "moment";

enum Page {
  Summary,
  Booking,
  CallMe,
  Expired,
  Confirm,
};

interface EntryRecord {
  date: string,
  timeCode: string,
  entry: CalendarEntry
};

interface TimeSlot {
  code: string,
  startTime: string,
  endTime: string,
};

const timeSlots: Record<string, TimeSlot> = {
  "M": { code: "M", startTime: "8:00 AM", endTime: "12:00 PM" },
  "N": { code: "N", startTime: "12:00 PM", endTime: "4:00 PM" },
  "MorningExtended": { code: "MorningExtended", startTime: "8:00 AM", endTime: "1:00 PM" },
  "AfternoonExtended": { code: "AfternoonExtended", startTime: "1:00 PM", endTime: "6:00 PM" },
  "LateMorning": { code: "LateMorning", startTime: "10:00 AM", endTime: "1:00 PM" },
  "EarlyAfternoon": { code: "EarlyAfternoon", startTime: "1:00 PM", endTime: "4:00 PM" }
};

const getTimeSlotDisplayFromCode = (code: string) => {
  const timeSlot = timeSlots[code];
  return `${timeSlot.startTime} - ${timeSlot.endTime}`;
};

const formatDate = (date: string) => moment(date).format("ddd MM/DD/YY");

const getAgentWord = (data: AutomaticAppointmentDto) => data.appointmentInformation.isAppraisal ? "Broker" : "Agent";

const PolicyHolderBooking = () => {
  const [page, setPage] = useState(Page.Summary);
  const [data, setData] = useState<AutomaticAppointmentDto>();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  let params = useParams();

  useEffect(() => {
    getAppointmentInformation(params.workItemId, params.campaign, params.routeKey, (data: AutomaticAppointmentDto) => {
      setData(data);

      switch (data.appointmentInformation.appointmentStatus) {
        case AutomaticAppointmentStatus.Completed_DateChosen:
          setSelectedDate(data.appointmentInformation.apptDate);
          setSelectedTime(getTimeSlotDisplayFromCode(data.appointmentInformation.apptTimeCode));
          setPage(Page.Confirm);
          break;
        case AutomaticAppointmentStatus.Completed_CallMe:
          setPage(Page.CallMe);
          break;
        case AutomaticAppointmentStatus.Canceled_Timeout:
        case AutomaticAppointmentStatus.Canceled_StatusChange:
          setPage(Page.Expired);
          break;
      }
    });
  }, [params]);

  const onConfirmClicked = (date: string, timeCode: string) => {
    setSelectedDate(date);
    setSelectedTime(getTimeSlotDisplayFromCode(timeCode));
    postAppointmentSelection(params.workItemId, params.campaign, params.routeKey, { callMeRequested: false, dateSelected: date, timeRangeCode: timeCode }, () => setPage(Page.Confirm));
  };

  const onRequestCallbackClicked = () => postAppointmentSelection(params.workItemId, params.campaign, params.routeKey, { callMeRequested: true }, () => setPage(Page.CallMe));
  const onContactUsClicked = () => {
    const a = document.createElement("a");
    a.href = "tel:+18005656063";
    a.click();
   };

  const enumToPageMapping = {
    [Page.Summary]: <SummaryPage
                      data={data}
                      onBookClicked={() => setPage(Page.Booking)}
                      onRequestCallbackClicked={onRequestCallbackClicked}
                      onAboutClicked={() => window.location.href = "https://www2.muellerreports.com"}
                      onContactUsClicked={onContactUsClicked}
                    />,
    [Page.Booking]: <BookingPage data={data} onConfirmClicked={onConfirmClicked} onRequestCallbackClicked={onRequestCallbackClicked} onContactUsClicked={onContactUsClicked} />,
    [Page.CallMe]: <CallMePage />,
    [Page.Expired]: <ExpiredPage />,
    [Page.Confirm]: <ConfirmationPage data={data} date={selectedDate} time={selectedTime} />,
  };

  return (
    <div className="policy-holder-booking">
      <div className="logos">
        <img src="//www2.muellerreports.com/wp-content/uploads/2016/11/Mueller-logo-web.png" alt="Mueller Logo" />
        {!logoLoadFailed && data?.appointmentInformation?.logoUrl && <img src={data.appointmentInformation.logoUrl} alt="Customer Logo" onError={() => setLogoLoadFailed(true)} /> /* we dont know if the image exists or not, the server sends back a url either way, so we will just try to load it */}
      </div>
      {enumToPageMapping[page]}
    </div>
  );
};

interface SummaryProps {
  data: AutomaticAppointmentDto;
  onBookClicked: () => void;
  onRequestCallbackClicked: () => void;
  onAboutClicked: () => void;
  onContactUsClicked: () => void;
};

const SummaryPage = ({ data, onBookClicked, onRequestCallbackClicked, onAboutClicked, onContactUsClicked }: SummaryProps) => {
  const [isPostingCallMe, setIsPostingCallMe] = useState(false);

  const onRequestCallbackClickedWrapped = () => {
    setIsPostingCallMe(true);
    onRequestCallbackClicked();
  };

  return (
    <>
      <DataCard
        title={data?.appointmentInformation.policyHolderName ?? "Loading..."}
        isLoading={!data}
        data={data}
        fields={[
          { label: "Address", property: "appointmentInformation.address" },
          { label: "Company", property: "appointmentInformation.company" },
          data?.appointmentInformation?.agent && { label: getAgentWord(data), property: "appointmentInformation.agent" },
        ]}
      />
      {
        (!data || (!data?.appointmentInformation?.isWithinCallCenterHours)) &&
        (
          <p>
            The Mueller Customer service team is available at 1-800-565-6063 to answer any of your questions Monday through Friday 8am-7pm EST and Sat 8am-4pm EST.
            <br />
            <br />
            Alternately, you may request a callback or book your appointment via the appropriate button below.
          </p>
        )
      }
      <Button disabled={isPostingCallMe || !data} startIcon={<DateRange />} onClick={onBookClicked}>Book Appointment</Button>
      <Button disabled={isPostingCallMe || !data} isLoading={isPostingCallMe} startIcon={<PhoneCallback />} onClick={onRequestCallbackClickedWrapped}>Request Callback</Button>
      {
        (!data && <Button disabled={true} startIcon={<Phone />}>Contact Us</Button>)
        ||
        (
          data?.appointmentInformation?.isWithinCallCenterHours &&
          <Button disabled={isPostingCallMe || !data} startIcon={<Phone />} onClick={onContactUsClicked}>Contact Us</Button>
        )
      }      
      <Button disabled={isPostingCallMe || !data} startIcon={<Info />} onClick={onAboutClicked}>About Mueller</Button>
    </>
  );
};

interface BookingProps {
  data: AutomaticAppointmentDto;
  onConfirmClicked: (date: string, timeCode: string) => void;
  onRequestCallbackClicked: () => void;
  onContactUsClicked: () => void;
};

type LoadingState = "not-loading" | "posting-call" | "posting-confirm";

const BookingPage = ({ data, onConfirmClicked, onRequestCallbackClicked, onContactUsClicked }: BookingProps) => {
  const [entries, setEntries] = useState<EntryRecord[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<EntryRecord>();
  const [minDate, setMinDate] = useState<Moment>();
  const [maxDate, setMaxDate] = useState<Moment>();
  const [loadingState, setLoadingState] = useState<LoadingState>("not-loading");

  const onConfirmClickedWrapped = () => {
    onConfirmClicked(selectedEntry.date, selectedEntry.timeCode);
    setLoadingState("posting-confirm");
  };

  const onRequestCallbackClickedWrapped = () => {
    onRequestCallbackClicked();
    setLoadingState("posting-call");
  };

  useEffect(() => {
    let i = 0;

    let entries = data.availableAppointmentDates.map(d =>
      ({
        date: d.date,
        timeCode: d.appointmentTimeRangeCode,
        entry: new CalendarEntry({
          startDate: moment(`${d.date} ${timeSlots[d.appointmentTimeRangeCode].startTime}`, "YYYY-MM-DD hh:mm A"),
          endDate: moment(`${d.date} ${timeSlots[d.appointmentTimeRangeCode].endTime}`, "YYYY-MM-DD hh:mm A"),
          id: i++,
          timeType: CalendarTimeType.Available,
        })
      }));
    
    setMinDate(moment(data.availableAppointmentDates[0].date, "YYYY-MM-DD"));
    setMaxDate(moment(data.availableAppointmentDates[data.availableAppointmentDates.length - 1].date, "YYYY-MM-DD"));
    setEntries(entries);
  }, [data]);

  const onEntryClicked = (entry: CalendarEntry) => setSelectedEntry(entries.find(e => e.entry.id === entry.id));

  // need the min date loaded so we can start the calendar on the right page
  if (!minDate) return null;

  return <>
    <Calendar
      startDate={minDate}
      calendarEntries={entries.map(e => new CalendarEntry({
        ...e.entry,
        className: e.entry.id === selectedEntry?.entry.id ? "selected" : "",
      }))}
      onEntryClicked={onEntryClicked}
      dragToCreateEntries={false}
      disableViewSwitching
      disableDateSelection
      calendarTimeStart={8}
      calendarTimeEnd={17}
      initialViewType={CalendarViewType.Week}
      minDate={minDate}
      maxDate={maxDate}
    />
    <Button
      disabled={!selectedEntry || loadingState !== "not-loading"}
      isLoading={loadingState === "posting-confirm"}
      onClick={onConfirmClickedWrapped}
    >
        Confirm
    </Button>
    <Button
      disabled={loadingState !== "not-loading"}
      isLoading={loadingState === "posting-call"}
      startIcon={<PhoneCallback />}
      onClick={onRequestCallbackClickedWrapped}
    >
        Request Callback Instead
    </Button>
    {
      data?.appointmentInformation?.isWithinCallCenterHours &&
      <Button
        disabled={loadingState !== "not-loading"}
        isLoading={loadingState === "posting-call"}
        startIcon={<Phone />}
        onClick={onContactUsClicked}
      >
          Contact Us Instead
      </Button>
    }
  </>;
};

const CallMePage = () =>
  <div className="call-me">
    <h1 className="centered">We'll be in touch...</h1>
    <div className="display-text">
      A Mueller Field Representative will be contacting you to review any questions or concerns you may have.
      <br />
      <br />
      If the available dates didn't work for you, we'll review what other accommodations might be available.
    </div>
  </div>;

const ExpiredPage = () =>
  <div className="call-me">
    <h1 className="centered">Appointment Link Has Expired</h1>
    <div className="display-text">
      A Mueller Field Representative will be contacting you to review the next steps and address any questions you may have.
    </div>
  </div>;

interface ConfirmationProps {
  data: AutomaticAppointmentDto;
  date: string,
  time: string,
};

const ConfirmationPage = ({ data, date, time }: ConfirmationProps) =>
  <div className="confirmation">
    <h1 className="centered">Your request for an appointment is awaiting confirmation!</h1>
    <DataCard
      title={data?.appointmentInformation.policyHolderName ?? "Loading..."}
      data={data}
      fields={[
        { label: "Address", property: "appointmentInformation.address" },
        { label: "Company", property: "appointmentInformation.company" },
        data?.appointmentInformation?.agent && { label: getAgentWord(data), property: "appointmentInformation.agent" },
        { label: "Date", value: formatDate(date) },
        { label: "Time Window", value: time },
      ]}
    />
    <div className="display-text">
      A Mueller representative will contact you to confirm this request can be met or schedule a different date and time. 
      If you have any questions or concerns please contact customer service (<a href="tel:800-875-8339">1-800-875-8339</a>).
    </div>
  </div>

export default PolicyHolderBooking;
